import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getEnabledElement, Enums as CoreEnums } from "@cornerstonejs/core";
import { Enums as csToolsEnums } from "@cornerstonejs/tools";
import { isMobile } from "react-device-detect";
import ViewportContext from "../../../../../context/studyviewer/ViewportContext";
import themeContext from "../../../../../context/theme/ThemeContext";
import ModalMain from "../../../../../components/Modal/ModalMain";
import ModalHeader from "../../../../../components/Modal/ModalHeader";
import ModalFooter from "../../../../../components/Modal/ModalFooter";

const VERTEBRA_SEQUENCE = [
  "C1", "C2", "C3", "C4", "C5", "C6", "C7",
  "T1", "T2", "T3", "T4", "T5", "T6", "T7", "T8", "T9", "T10", "T11", "T12",
  "L1", "L2", "L3", "L4", "L5",
  "S1",
];

const REGION_GROUPS = [
  { label: "Cervical",  vertebrae: ["C1","C2","C3","C4","C5","C6","C7"],                              color: "#4FC3F7" },
  { label: "Thoracic",  vertebrae: ["T1","T2","T3","T4","T5","T6","T7","T8","T9","T10","T11","T12"], color: "#81C784" },
  { label: "Lumbar",    vertebrae: ["L1","L2","L3","L4","L5"],                                        color: "#FFB74D" },
  { label: "Sacral",    vertebrae: ["S1"],                                                             color: "#E57373" },
];

const REGION_COLORS = { C: "#4FC3F7", T: "#81C784", L: "#FFB74D", S: "#E57373" };
const getColor = (label) => REGION_COLORS[label?.[0]] || "#FFFFFF";

const DEFAULT_START_VERTEBRA = "C1";

const SpineLabellingTool = ({ viewportIndex, viewportElement }) => {
  const {
    isSpineLabellingActive,
    setIsSpineLabellingActive,
    activeViewportIndex,
    spineLabels,
    setSpineLabels,
    spinePhase,
    setSpinePhase,
    handleDefaultToolChange,
  } = useContext(ViewportContext);
  const { theme, getThemeColor, getAlterColor } = useContext(themeContext);

  const [startVertebra, setStartVertebra] = useState(DEFAULT_START_VERTEBRA);
  const [direction, setDirection] = useState("descending");
  const [canvasPositions, setCanvasPositions] = useState([]);
  const [svgSize, setSvgSize] = useState({ width: 0, height: 0 });
  const [contextMenu, setContextMenu] = useState(null); // { x, y, index }
  const [viewportFrameOfReference, setViewportFrameOfReference] = useState(null);
  const draggingRef = useRef(null); // { index } while dragging a label
  const canvasPositionsRef = useRef([]);
  // Number of labels that existed when "Start Labelling" was clicked for this
  // session. Used so getNextVertebra starts from startVertebra even when labels
  // from a previous session are already present.
  const sessionBaseLabelCountRef = useRef(0);

  const isActiveViewport = viewportIndex === activeViewportIndex;

  const spineLabelsRef = useRef(spineLabels);
  spineLabelsRef.current = spineLabels;

  // A label is visible in a viewport only if the viewport's current, live
  // FrameOfReferenceUID matches the FrameOfReferenceUID it was placed under —
  // an unrelated series (e.g. a secondary-capture image in another viewport)
  // must never show another series' spine labels. Since FrameOfReferenceUID
  // is frequently missing (CR/DX/SC commonly have none), undefined is treated
  // as "unknown", never as a match — two undefined values are NOT considered
  // equal here (unlike plain === ). The only exception is the exact viewport
  // a label was placed in, which always shows it regardless of whether a
  // FrameOfReferenceUID could be determined, so the tool stays usable on
  // modalities that never expose one.
  const visibleLabels = useMemo(
    () =>
      spineLabels
        .map((lbl, index) => ({ lbl, index }))
        .filter(({ lbl }) => {
          if (lbl.viewportIndex === viewportIndex) return true;
          return Boolean(
            lbl.frameOfReferenceUID &&
            viewportFrameOfReference &&
            lbl.frameOfReferenceUID === viewportFrameOfReference
          );
        }),
    [spineLabels, viewportFrameOfReference, viewportIndex]
  );
  const visibleLabelsRef = useRef(visibleLabels);
  visibleLabelsRef.current = visibleLabels;
  const hasVisibleLabels = visibleLabels.length > 0;

  const getNextVertebra = useCallback(
    (currentLabels) => {
      // Start from the user-selected vertebra both when there are no labels at all
      // and when we're at the beginning of a new session (labels from a prior
      // session exist but none have been placed yet in this session).
      if (currentLabels.length <= sessionBaseLabelCountRef.current) return startVertebra;
      const last = currentLabels[currentLabels.length - 1].vertebra;
      const idx = VERTEBRA_SEQUENCE.indexOf(last);
      if (direction === "descending") {
        if (idx < 0 || idx >= VERTEBRA_SEQUENCE.length - 1) return null;
        return VERTEBRA_SEQUENCE[idx + 1];
      } else {
        if (idx <= 0) return null;
        return VERTEBRA_SEQUENCE[idx - 1];
      }
    },
    [startVertebra, direction]
  );

  // ── Canvas position sync for THIS viewport ──
  const updateCanvasPositions = useCallback(() => {
    if (!viewportElement) return;
    try {
      const enabled = getEnabledElement(viewportElement);
      if (!enabled?.viewport) return;
      const { viewport } = enabled;
      setViewportFrameOfReference(viewport.getFrameOfReferenceUID?.() || null);
      const rect = viewportElement.getBoundingClientRect();
      setSvgSize({ width: rect.width, height: rect.height });
      const positions = visibleLabelsRef.current.map(({ lbl }) =>
        viewport.worldToCanvas(lbl.worldPos)
      );
      canvasPositionsRef.current = positions;
      setCanvasPositions(positions);
    } catch (_) {}
  }, [viewportElement]);

  const hasLabels = spineLabels.length > 0;

  useEffect(() => {
    if (!viewportElement || !hasLabels) return;
    updateCanvasPositions();
    const sync = () => updateCanvasPositions();
    viewportElement.addEventListener(CoreEnums.Events.CAMERA_MODIFIED, sync);
    viewportElement.addEventListener(CoreEnums.Events.IMAGE_RENDERED, sync);
    return () => {
      viewportElement.removeEventListener(CoreEnums.Events.CAMERA_MODIFIED, sync);
      viewportElement.removeEventListener(CoreEnums.Events.IMAGE_RENDERED, sync);
    };
  }, [viewportElement, hasLabels, updateCanvasPositions]);

  useEffect(() => {
    updateCanvasPositions();
  }, [spineLabels, viewportFrameOfReference, updateCanvasPositions]);

  // ── Drag-to-move labels: active when tool closed OR in complete phase ──
  useEffect(() => {
    const enableDrag =
      hasVisibleLabels &&
      !!viewportElement &&
      (!isSpineLabellingActive || spinePhase === "complete");
    if (!enableDrag) return;
    const onMouseMove = (e) => {
      if (!draggingRef.current) return;
      try {
        const enabled = getEnabledElement(viewportElement);
        if (!enabled?.viewport) return;
        const rect = viewportElement.getBoundingClientRect();
        const canvasX = e.clientX - rect.left;
        const canvasY = e.clientY - rect.top;
        const worldPos = enabled.viewport.canvasToWorld([canvasX, canvasY]);
        const { index } = draggingRef.current;
        setSpineLabels((prev) =>
          prev.map((lbl, i) => (i === index ? { ...lbl, worldPos } : lbl))
        );
      } catch (_) {}
    };
    const onMouseUp = () => { draggingRef.current = null; };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [isSpineLabellingActive, spinePhase, hasVisibleLabels, viewportElement, setSpineLabels]);

  // drag labels using Cornerstone's OWN normalized touch events
  // (TOUCH_START/TOUCH_DRAG/TOUCH_END) — the same pipeline that makes annotations
  // draggable on touch, so it is smooth. We listen on the viewport's PARENT in the
  // CAPTURE phase (runs before Cornerstone's tool listeners on the viewport); when the
  // touch lands on a label we stopPropagation to keep the active tool (pan/scroll/WL)
  // from also acting, and move the label to event.detail.currentPoints.world.
  useEffect(() => {
    if (!isMobile) return;
    const enableDrag =
      hasVisibleLabels &&
      !!viewportElement &&
      (!isSpineLabellingActive || spinePhase === "complete");
    if (!enableDrag) return;
    const parent = viewportElement.parentElement;
    if (!parent) return;

    // canvasPositionsRef/visibleLabelsRef are aligned to this viewport's
    // visible-label subset, so map the hit-tested position back to the index
    // in the full spineLabels array before returning it.
    const hitTestIndex = (detail) => {
      const canvas = detail?.currentPoints?.canvas;
      if (!canvas) return -1;
      const positions = canvasPositionsRef.current || [];
      const visible = visibleLabelsRef.current || [];
      let best = -1;
      let bestDist = 32; // finger-friendly radius (px)
      positions.forEach((pos, idx) => {
        if (!pos) return;
        const d = Math.hypot(pos[0] - canvas[0], pos[1] - canvas[1]);
        if (d < bestDist) { bestDist = d; best = idx; }
      });
      return best < 0 ? -1 : (visible[best]?.index ?? -1);
    };

    const onDown = (evt) => {
      const idx = hitTestIndex(evt.detail);
      if (idx < 0) return; // not on a label -> let Cornerstone handle normally
      draggingRef.current = { index: idx };
      evt.stopPropagation();
    };
    const onDrag = (evt) => {
      if (!draggingRef.current) return;
      const canvas = evt.detail?.currentPoints?.canvas;
      if (!canvas) return;
      try {
        const enabled = getEnabledElement(viewportElement);
        if (!enabled?.viewport) return;
        const worldPos = enabled.viewport.canvasToWorld([canvas[0], canvas[1]]);
        const { index } = draggingRef.current;
        setSpineLabels((prev) =>
          prev.map((lbl, i) => (i === index ? { ...lbl, worldPos } : lbl))
        );
      } catch (_) {}
      evt.stopPropagation();
    };
    const onUp = () => { draggingRef.current = null; };

    const opts = { capture: true };
    parent.addEventListener(csToolsEnums.Events.TOUCH_START, onDown, opts);
    parent.addEventListener(csToolsEnums.Events.TOUCH_DRAG, onDrag, opts);
    parent.addEventListener(csToolsEnums.Events.TOUCH_END, onUp, opts);
    return () => {
      parent.removeEventListener(csToolsEnums.Events.TOUCH_START, onDown, opts);
      parent.removeEventListener(csToolsEnums.Events.TOUCH_DRAG, onDrag, opts);
      parent.removeEventListener(csToolsEnums.Events.TOUCH_END, onUp, opts);
    };
  }, [isSpineLabellingActive, spinePhase, hasVisibleLabels, viewportElement, setSpineLabels]);

  // ── Mouse placement handler — active for every viewport in placing phase ──
  useEffect(() => {
    if (!viewportElement || spinePhase !== "placing") return;
    const onMouseDown = (evt) => {
      if (evt.button !== 0) return;
      evt.stopPropagation();
      try {
        const enabled = getEnabledElement(viewportElement);
        if (!enabled?.viewport) return;
        const { viewport } = enabled;
        const rect = viewportElement.getBoundingClientRect();
        const canvasX = evt.clientX - rect.left;
        const canvasY = evt.clientY - rect.top;
        const worldPos = viewport.canvasToWorld([canvasX, canvasY]);
        const frameOfReferenceUID = viewport.getFrameOfReferenceUID?.() || null;
        setSpineLabels((prev) => {
          const next = getNextVertebra(prev);
          if (!next) return prev;
          return [...prev, { worldPos, vertebra: next, excluded: false, frameOfReferenceUID, viewportIndex }];
        });
      } catch (_) {}
    };
    viewportElement.addEventListener("mousedown", onMouseDown, true);
    return () => viewportElement.removeEventListener("mousedown", onMouseDown, true);
  }, [viewportElement, spinePhase, getNextVertebra, setSpineLabels, viewportIndex]);

  // ── Escape: stop labelling if placing, otherwise close tool ──
  useEffect(() => {
    if (!isSpineLabellingActive) return;
    const onKeyDown = (evt) => {
      if (evt.key !== "Escape") return;
      if (spinePhase === "placing") {
        setSpinePhase("complete");
      } else {
        handleClose();
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSpineLabellingActive, spinePhase]);

  // ── When tool is deactivated externally, reset phase only ──
  useEffect(() => {
    if (!isSpineLabellingActive) {
      setSpinePhase("select");
      setStartVertebra(DEFAULT_START_VERTEBRA);
      setDirection("descending");
    }
  }, [isSpineLabellingActive, setSpinePhase]);

  // ── Close context menu on outside click ──
  useEffect(() => {
    if (!contextMenu) return;
    const onClose = () => setContextMenu(null);
    window.addEventListener("click", onClose, false);
    window.addEventListener("contextmenu", onClose, false);
    return () => {
      window.removeEventListener("click", onClose, false);
      window.removeEventListener("contextmenu", onClose, false);
    };
  }, [contextMenu]);

  const handleClose = () => {
    setIsSpineLabellingActive(false);
    handleDefaultToolChange();
  };

  const handleReset = () => {
    setSpineLabels([]);
    setSpinePhase("select");
    setStartVertebra(DEFAULT_START_VERTEBRA);
    setDirection("descending");
  };

  const handleStartPlacing = () => {
    sessionBaseLabelCountRef.current = spineLabels.length;
    setSpinePhase("placing");
  };

  const handleReselect = () => {
    setSpinePhase("select");
  };

  const handleContextMenu = useCallback((evt, index) => {
    evt.preventDefault();
    evt.stopPropagation();
    if (!viewportElement) return;
    const rect = viewportElement.getBoundingClientRect();
    setContextMenu({ x: evt.clientX - rect.left, y: evt.clientY - rect.top, index });
  }, [viewportElement]);

  const handleLabelMouseEnter = useCallback(() => {
    if (viewportElement) viewportElement.style.cursor = "move";
  }, [viewportElement]);

  const handleLabelMouseLeave = useCallback(() => {
    if (viewportElement) viewportElement.style.cursor = "";
  }, [viewportElement]);

  const handleRemoveLabel = () => {
    if (contextMenu === null) return;
    setSpineLabels((prev) => prev.filter((_, i) => i !== contextMenu.index));
    setContextMenu(null);
  };

  const textColor = theme?.palette?.inactiveTool || "#CCCCCC";
  const activeColor = theme?.palette?.activeTool || "#0078D4";
  const panelBg = getThemeColor("action-details-background");
  const borderColor = theme?.palette?.border || "#585858";
  const hoverBg = getThemeColor("popup-hover");
  const unselectedBg = getAlterColor("rgba(255,255,255,0.06)", "rgba(0,0,0,0.05)");

  const renderLabels = () =>
    visibleLabels.map(({ lbl, index }, i) => {
      const pos = canvasPositions[i];
      if (!pos) return null;
      const [x, y] = pos;
      const color = getColor(lbl.vertebra);
      return (
        <g
          key={index}
          style={{
            // On mobile the glyph must NOT capture the touch — let it fall through to
            // the Cornerstone viewport so its TOUCH_START/TOUCH_DRAG events fire and the
            // effect below drags the label (same pipeline as annotations). Desktop keeps
            // the direct mouse drag.
            pointerEvents: isMobile ? "none" : "all",
            cursor: "move",
            userSelect: "none",
          }}
          onMouseDown={(e) => {
            if (e.button !== 0) return;
            e.stopPropagation();
            draggingRef.current = { index };
          }}
          onContextMenu={(e) => handleContextMenu(e, index)}
          onMouseEnter={handleLabelMouseEnter}
          onMouseLeave={handleLabelMouseLeave}
        >
          <circle cx={x} cy={y} r={4} fill={color} />
          <rect x={x + 8} y={y - 9} width={26} height={14} rx={3} fill={color} opacity={0.9} />
          <text x={x + 21} y={y + 1} textAnchor="middle" fill="#000" fontSize={10} fontWeight="700" fontFamily="sans-serif">
            {lbl.vertebra}
          </text>
        </g>
      );
    });

  const renderContextMenu = () => {
    if (!contextMenu) return null;
    return (
      <div
        style={{
          position: "absolute",
          top: contextMenu.y,
          left: contextMenu.x,
          background: panelBg,
          border: `1px solid ${borderColor}`,
          borderRadius: 4,
          boxShadow: "0 2px 8px rgba(0,0,0,0.5)",
          zIndex: 300,
          pointerEvents: "auto",
          minWidth: 140,
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
        onContextMenu={(e) => e.preventDefault()}
      >
        <div
          onClick={handleRemoveLabel}
          style={{ padding: "8px 12px", cursor: "pointer", color: "#E57373", fontSize: 12 }}
          onMouseEnter={(e) => { e.currentTarget.style.background = hoverBg; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
        >
          Remove label
        </div>
      </div>
    );
  };

  // Tool closed but labels exist → SVG overlay only (labels persist on image).
  // Only render in a viewport actually showing the relevant series.
  if (!isSpineLabellingActive) {
    if (!hasVisibleLabels) return null;
    return (
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, zIndex: 200, pointerEvents: "none" }}>
        <svg width={svgSize.width} height={svgSize.height} style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none", overflow: "visible" }}>
          {renderLabels()}
        </svg>
        {renderContextMenu()}
      </div>
    );
  }

  // ── SELECT phase: modal — only on active viewport ──
  if (spinePhase === "select") {
    if (!isActiveViewport) return null;
    const modalDetails = {
      header: (
        <ModalHeader
          updatedModal={true}
          heading="Spine Labelling"
          handleCancel={handleClose}
        />
      ),
      body: (
        <SpineLabellingModalBody
          startVertebra={startVertebra}
          setStartVertebra={setStartVertebra}
          direction={direction}
          setDirection={setDirection}
          activeColor={activeColor}
          textColor={textColor}
          borderColor={borderColor}
          unselectedBg={unselectedBg}
        />
      ),
      footer: (
        <ModalFooter
          PrimaryButtonList={[{ title: "Start Labelling", onclick: handleStartPlacing }]}
          DefaultButtonList={[{ title: "Close", onclick: handleClose }]}
        />
      ),
      width: "480px",
    };
    return (
      <ModalMain
        updatedModal={true}
        modalDetails={modalDetails}
        showModal={true}
        scrollableBody={true}
      />
    );
  }

  // ── PLACING / COMPLETE: SVG overlay on every viewport, control panel on active only ──
  return (
    <div
      style={{
        position: "absolute",
        top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 200,
        pointerEvents: "none",
      }}
    >
      <svg
        width={svgSize.width}
        height={svgSize.height}
        style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none", overflow: "visible" }}
      >
        {renderLabels()}
      </svg>

      {renderContextMenu()}

      {/* Control panel — active viewport only */}
      {isActiveViewport && (
        <div
          style={{
            position: "absolute",
            top: 8, right: 8,
            background: panelBg,
            border: `1px solid ${borderColor}`,
            borderRadius: 6,
            padding: "10px 12px",
            minWidth: 170,
            maxHeight: "70%",
            display: "flex",
            flexDirection: "column",
            pointerEvents: "auto",
            boxShadow: "0 2px 12px rgba(0,0,0,0.5)",
            zIndex: 210,
          }}
        >
          {/* Header row */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <span style={{ color: textColor, fontSize: 12, fontWeight: 600 }}>
              {spinePhase === "placing" ? "Placing Labels…" : "Spine Labels"}
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {spinePhase === "placing" && (
                <button
                  title="Change start label"
                  onClick={handleReselect}
                  style={{
                    background: "none", border: "none", color: textColor,
                    cursor: "pointer", lineHeight: 1, padding: "2px",
                    display: "flex", alignItems: "center",
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 2.5a1 1 0 011-1h6l3.5 4-3.5 4H2a1 1 0 01-1-1v-6z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
                    <circle cx="4.5" cy="6.5" r="1.1" fill="currentColor"/>
                  </svg>
                </button>
              )}
              <button
                title="Reset labels"
                onClick={handleReset}
                style={{
                  background: "none", border: "none", color: "#E57373",
                  cursor: "pointer", fontSize: 22, lineHeight: 1, padding: "2px",
                }}
              >
                ↺
              </button>
              <button
                onClick={handleClose}
                style={{ background: "none", border: "none", color: textColor, cursor: "pointer", fontSize: 22, padding: "2px" }}
              >
                ×
              </button>
            </div>
          </div>

          {spinePhase === "placing" && (
            <div style={{ color: textColor, fontSize: 11, opacity: 0.6, marginBottom: 8 }}>
              Click to place. Press Escape or Done to finish.
            </div>
          )}

          {spinePhase === "placing" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <button
                onClick={handleClose}
                style={{ padding: "6px 0", background: activeColor, color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12, fontWeight: 600 }}
              >
                Done Labelling
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SpineLabellingModalBody = ({ startVertebra, setStartVertebra, direction, setDirection, activeColor, textColor, borderColor, unselectedBg }) => {

  return (
    <div style={{ padding: "20px 24px" }}>
      {REGION_GROUPS.map((group) => (
        <div key={group.label} style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <span
              style={{
                display: "inline-block", width: 10, height: 10,
                borderRadius: "50%", background: group.color, flexShrink: 0,
              }}
            />
            <span style={{ color: group.color, fontSize: 12, fontWeight: 600 }}>
              {group.label}
            </span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {group.vertebrae.map((v) => {
              const isSelected = startVertebra === v;
              return (
                <button
                  key={v}
                  onClick={() => setStartVertebra(v)}
                  style={{
                    padding: "4px 8px",
                    minWidth: 36,
                    background: isSelected ? group.color : unselectedBg,
                    color: isSelected ? "#000" : textColor,
                    border: isSelected ? `1.5px solid ${group.color}` : `1px solid ${borderColor}`,
                    borderRadius: 4,
                    cursor: "pointer",
                    fontSize: 12,
                    fontWeight: isSelected ? 700 : 400,
                    transition: "all 0.15s",
                  }}
                >
                  {v}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <div style={{ marginTop: 4, paddingTop: 16, borderTop: `1px solid ${borderColor}` }}>
        <div style={{ color: textColor, fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
          Direction
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {[
            { key: "ascending",  label: "↑  Ascending" },
            { key: "descending", label: "↓  Descending" },
          ].map(({ key, label }) => {
            const isSelected = direction === key;
            return (
              <button
                key={key}
                onClick={() => setDirection(key)}
                style={{
                  flex: 1,
                  padding: "7px 8px",
                  background: isSelected ? activeColor : unselectedBg,
                  color: isSelected ? "#fff" : textColor,
                  border: isSelected ? `1.5px solid ${activeColor}` : `1px solid ${borderColor}`,
                  borderRadius: 4,
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: isSelected ? 600 : 400,
                  transition: "all 0.15s",
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <p style={{ color: textColor, fontSize: 11, opacity: 0.7, margin: "16px 0 0" }}>
        Starting from <strong style={{ opacity: 1 }}>{startVertebra}</strong>. Click each vertebra on the image. Press Escape or Done to finish.
      </p>
    </div>
  );
};

export default SpineLabellingTool;
